import { FC } from 'react';
import cls from 'classnames';
import { type Activity, ActivityType, RuleConfig } from '@/types/home';
import { useScreenType } from '@web3/base-hooks';
import {
  domainAbsolutePathReplace,
  openLink,
  replaceBybitToLocationHost,
} from '@web3/base-utils';
import { Page } from '@/config/track-config';
import { useCountDown } from 'ahooks';
import { Tag } from '../tag';
import styles from './index.module.scss';

interface CardProps {
  item: Activity;
  index: number;
}

const getTimeItem = (time: number) => {
  return time < 10 ? `0${time}` : `${time}`;
};

const useCountdownFmt = (times: number[] = []) => {
  const nextTime = times.find((time) => time > Date.now());
  const [ts, res] = useCountDown({ targetDate: nextTime || Date.now() });
  // console.log('countdownData ts, res:', ts, res);
  if (ts <= 0) {
    return null;
  }
  const hourDisplay = ts > 3600 * 1000;
  if (hourDisplay) {
    return `${getTimeItem(res.days)} D ${getTimeItem(res.hours)} H`;
  }
  return `${getTimeItem(res.minutes)} M ${getTimeItem(res.seconds)} S`;
};

export const Card: FC = ({ item, index }: CardProps) => {
  const isIdo = item?.activityType === ActivityType.IDO;
  const { isMobile } = useScreenType();

  const categoryMap: Record<ActivityType, string> = {
    [ActivityType.Staking]: 'Staking',
    [ActivityType.IDO]: 'IDO',
    [ActivityType.NFT]: 'NFT',
    [ActivityType.Inscription]: 'Inscription',
    [ActivityType.AirDrip]: 'AirDrop',
    [ActivityType.Metropolis]: 'Metropolis',
    [ActivityType.Points]: 'Points',
    [ActivityType.DexPro]: 'DEX Pro',
    [ActivityType.Swap]: 'Swap',
    [ActivityType.WSOT]: 'WSOT',
  };

  const categoryClsMap: Record<ActivityType, string> = {
    [ActivityType.Staking]: styles.yellow,
    [ActivityType.IDO]: styles.blue,
    [ActivityType.NFT]: styles.yellow,
    [ActivityType.Inscription]: styles.blue,
    [ActivityType.AirDrip]: styles.green,
    [ActivityType.Metropolis]: styles.green,
    [ActivityType.Points]: styles.yellow,
    [ActivityType.DexPro]: styles.purple,
    [ActivityType.Swap]: styles.yellow,
    [ActivityType.WSOT]: styles.cyan,
  };

  const ruleConfog: RuleConfig = item?.activityRuleConfig || ({} as RuleConfig);

  const countdownData = useCountdownFmt(
    isIdo
      ? [
          ruleConfog.registrationStartTime,
          ruleConfog.registrationEndTime,
          ruleConfog.snapshotStartTime,
          ruleConfog.snapshotEndTime,
          ruleConfog.lotteryStartTime,
          ruleConfog.lotteryEndTime,
          ruleConfog.redeemStartTime,
          ruleConfog.redeemEndTime,
        ]
      : [ruleConfog.activityStartTime, ruleConfog.activityEndTime]
  );

  if (!item) {
    return null;
  }

  const jumpTo = () => {
    if (isMobile) {
      openLink(item.appGoUrl);
      return;
    }
    if (item?.webGoUrl) {
      window.open(domainAbsolutePathReplace(item.webGoUrl), '_blank');
    }
  };

  return (
    <div
      className={styles.card}
      onClick={jumpTo}
      data-spm-even="Web3UserClick"
      data-spm-data={JSON.stringify({
        page_name: Page.Home,
        area: 'earn',
        button_name: 'card',
        item_name: item.activityName,
        display_tag: !!countdownData,
        params: isMobile ? item?.appGoUrl : item?.webGoUrl,
      })}
    >
      <img
        className={styles.image}
        alt="img"
        src={replaceBybitToLocationHost(
          isMobile ? item.appPicUrl : item.webPicUrl
        )}
      />
      <div className={cls(styles.category, categoryClsMap[item.activityType])}>
        #{categoryMap[item.activityType]}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{item.activityName}</div>
        <div className={styles.subTitle}>{item.activityDesc}</div>
      </div>
      <div className={styles.tags}>
        {ruleConfog.activityTag ? (
          <Tag icon="smile" text={ruleConfog.activityTag} />
        ) : null}
        {countdownData ? (
          <Tag
            icon={Number(index) < 2 ? 'bars' : 'countdown'}
            text={countdownData}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Card;
